import React, { useState } from "react";
import PropTypes from "prop-types";
import { useAutoPromote, setAutoPromoteEnabled } from "./useAutoPromote";
import "../../components/ui-components/DataTable.css";

// Strip the long flag prefix for a compact display label.
function shortFlag(key) {
  if (!key) return "—";
  return key.replace(/^forecast\.allocation_/, "").replace(/^forecast\./, "");
}

// Format an error fraction as a percentage; "—" when 0/absent.
function fmtErr(err) {
  if (err === null || err === undefined || err === 0) return "—";
  return `${(err * 100).toFixed(1)}%`;
}

// Short date for audit / timestamps.
function shortDate(at) {
  if (!at) return "—";
  const d = new Date(at);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString();
}

// status → Tailwind colour classes.
const STATUS_CLASSES = {
  watching: "bg-warning-soft text-warning",
  confirmed: "bg-success-soft text-success",
  reverted: "bg-surface-2 text-text-2",
  blocked: "bg-surface-2 text-text-2",
  champion: "bg-surface-2 text-text-2",
};

function StatusBadge({ status }) {
  const cls = STATUS_CLASSES[status] || STATUS_CLASSES.champion;
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold select-none ${cls}`}>
      {status || "champion"}
    </span>
  );
}

StatusBadge.propTypes = {
  status: PropTypes.string,
};

function OnOffPill({ on }) {
  return on ? (
    <span className="inline-flex items-center rounded-full bg-success-soft px-2 py-0.5 text-xs font-semibold text-success select-none">
      On
    </span>
  ) : (
    <span className="inline-flex items-center rounded-full bg-surface-2 px-2 py-0.5 text-xs font-semibold text-text-2 select-none">
      Off
    </span>
  );
}

OnOffPill.propTypes = {
  on: PropTypes.bool,
};

export default function AutoPromotePanel() {
  const { data, isLoading, refetch } = useAutoPromote();
  const [busy, setBusy] = useState(false);

  const enabled = !!data?.enabled;
  const eligible = data?.eligible || [];
  const flags = data?.flags || [];
  const audit = data?.audit || [];

  // Merge eligible flag keys with any state objects (eligible flags with no
  // state yet render as a champion row at streak 0).
  const flagByKey = {};
  flags.forEach((f) => {
    if (f && f.flag_key) flagByKey[f.flag_key] = f;
  });
  const rows = eligible.map((key) => flagByKey[key] || { flag_key: key, status: "champion" });

  const onToggle = async () => {
    setBusy(true);
    try {
      await setAutoPromoteEnabled(!enabled);
      await refetch();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-card border border-card-bd bg-card px-5 py-4 text-sm">
      {/* Header */}
      <div className="mb-3 flex items-center justify-between gap-4">
        <div>
          <span className="text-xs font-semibold uppercase tracking-wide text-text-3">
            Champion/Challenger Auto-Promotion
          </span>
          <p className="mt-0.5 text-xs text-text-3">
            Automated: flips are audited and auto-roll-back. Default off — recommendations
            still appear in the readiness panel above.
          </p>
        </div>
        <button
          type="button"
          onClick={onToggle}
          disabled={busy}
          aria-pressed={enabled}
          className={`rounded-ctrl border px-3 py-1 text-xs font-semibold disabled:opacity-50 ${
            enabled
              ? "border-success bg-success-soft text-success hover:bg-success-soft"
              : "border-border bg-surface-2 text-text hover:bg-surface-2"
          }`}
        >
          {busy ? "Saving…" : enabled ? "Enabled — turn off" : "Disabled — turn on"}
        </button>
      </div>

      {/* Loading */}
      {isLoading && (
        <p className="py-2 text-xs text-text-3">Loading auto-promotion state…</p>
      )}

      {/* Empty + disabled hint */}
      {!isLoading && rows.length === 0 && !enabled && (
        <p className="py-2 text-xs text-text-3">
          Enable auto-promote to start the nightly champion/challenger loop.
        </p>
      )}

      {/* Flags table */}
      {!isLoading && rows.length > 0 && (
        <div className="overflow-x-auto rounded-ctrl border border-card-bd">
          <table className="data-table min-w-full text-sm">
            <thead>
              <tr>
                <th>Flag</th>
                <th>Current</th>
                <th>Champion err</th>
                <th>Challenger err</th>
                <th>Streak</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((f) => (
                <tr key={f.flag_key}>
                  <td className="font-medium text-text">{shortFlag(f.flag_key)}</td>
                  <td>
                    <OnOffPill on={(f.current_value ?? 0) >= 0.5} />
                  </td>
                  <td className="text-text-2">{fmtErr(f.champion_err)}</td>
                  <td className="text-text-2">{fmtErr(f.challenger_err)}</td>
                  <td className="text-text-2">{f.consecutive_ready ?? 0}</td>
                  <td>
                    <StatusBadge status={f.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Recent activity */}
      {!isLoading && audit.length > 0 && (
        <div className="mt-4">
          <span className="text-xs font-semibold uppercase tracking-wide text-text-3">
            Recent activity
          </span>
          <ul className="mt-2 space-y-1">
            {audit.slice(0, 10).map((a, i) => (
              <li key={`${a.flag_key}-${a.at}-${i}`} className="text-xs text-text-2">
                <span className="text-text-3">{shortDate(a.at)}</span>
                {" · "}
                <span className="font-medium text-text-2">{shortFlag(a.flag_key)}</span>
                {" · "}
                <span className="font-semibold text-text">{a.action}</span>
                {a.verdict ? <span className="text-text-2">{` · ${a.verdict}`}</span> : null}
                {a.reason ? <span className="text-text-3">{` · ${a.reason}`}</span> : null}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
